import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Form, Input, Select, Button, Typography, Spin, Space, Row, Col, Badge } from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;

const EditMobileAppStore = () => {
  const { store_id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const fetchMobileAppStoreDetails = useCallback(async () => {
    if (!store_id) {
      notify.error({ message: "Mobile app store ID is required" });
      navigate("/mobile-app-store");
      return;
    }

    setLoadingData(true);
    try {
      const response = await masterService.getSingleMobileAppStore(store_id);

      if (response.data && response.data.status) {
        const mobileAppStoreData = response.data.data[0] || response.data.data;
        if (mobileAppStoreData && mobileAppStoreData.store_name) {
          form.setFieldsValue({
            store_name: mobileAppStoreData.store_name,
            store_key: mobileAppStoreData.store_key,
            flexible_version: mobileAppStoreData.flexible_version,
            flexible_build_number: mobileAppStoreData.flexible_build_number,
            immediate_version: mobileAppStoreData.immediate_version,
            immediate_build_number: mobileAppStoreData.immediate_build_number,
            is_active: mobileAppStoreData.is_active || "Y",
            is_maintenance: mobileAppStoreData.is_maintenance || "N",
            is_force_update: mobileAppStoreData.is_force_update || "N",
          });
        } else {
          notify.error({ message: "Mobile app store data not found" });
          navigate("/mobile-app-store");
        }
      } else {
        notify.error({
          message:
            response.data?.message ||
            "Failed to fetch mobile app store details",
        });
        navigate("/mobile-app-store");
      }
    } catch (error) {
      console.error("Error fetching mobile app store details:", error);
      notify.error({ message: "Error fetching mobile app store details" });
      navigate("/mobile-app-store");
    } finally {
      setLoadingData(false);
    }
  }, [store_id, form, navigate]);

  useEffect(() => {
    setPageTitle("Edit Mobile App Store");
    fetchMobileAppStoreDetails();
  }, [fetchMobileAppStoreDetails, setPageTitle]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        store_id: store_id,
      };

      const response = await masterService.updateMobileAppStore(
        store_id,
        payload
      );

      if (response.data && response.data.status) {
        notify.success({
          message:
            response.data.message || "Mobile app store updated successfully",
        });
        navigate("/mobile-app-store");
      } else {
        notify.error({
          message: response.data.message || "Failed to update mobile app store",
        });
      }
    } catch (error) {
      console.error("Error updating mobile app store:", error);
      notify.error({ message: error?.data?.message || "Error updating mobile app store" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/mobile-app-store");
  };

  return (
    <div className="min-h-screen">
      <Spin spinning={loadingData}>
        <Card
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
            className="space-y-6"
          >
            {/* Mobile App Store Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <AppstoreOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Mobile App Store Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Name
                      </span>
                    }
                    name="store_name"
                    rules={[
                      { required: true, message: "Please enter store name" },
                    ]}
                  >
                    <Input
                      placeholder="Enter store name"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Key
                      </span>
                    }
                    name="store_key"
                    rules={[
                      { required: true, message: "Please enter key value" },
                    ]}
                  >
                    <Input
                      placeholder="Enter key value"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Flexible Version
                      </span>
                    }
                    name="flexible_version"
                  >
                    <Input
                      placeholder="Enter flexible version"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Flexible Build Number
                      </span>
                    }
                    name="flexible_build_number"
                  >
                    <Input
                      placeholder="Enter flexible build number"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Immediate Version
                      </span>
                    }
                    name="immediate_version"
                  >
                    <Input
                      placeholder="Enter immediate version"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Immediate Build Number
                      </span>
                    }
                    name="immediate_build_number"
                  >
                    <Input
                      placeholder="Enter immediate build number"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="is_active"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Maintenance Mode
                      </span>
                    }
                    name="is_maintenance"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="warning" text="Yes" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="No" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Force Update
                      </span>
                    }
                    name="is_force_update"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="warning" text="Yes" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="No" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Updating..." : "Update Mobile App Store"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default EditMobileAppStore;
